import React, { useState } from "react";
import {
  Lightbulb,
  AlertTriangle,
  Users,
  BookOpen,
  Clock,
  Plus,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

export interface TaskSuggestion {
  type: "OVERDUE_HOMEWORK" | "LESSON_HOMEWORK_ISSUE" | "INDIVIDUAL_HOMEWORK_ISSUE";
  priority: "HIGH" | "MEDIUM" | "LOW";
  department: {
    id: string;
    name: string;
    color?: string;
  } | null;
  lesson?: {
    id: string;
    title: string;
  };
  stats: {
    overdueCount: number;
    incompleteCount: number;
    studentCount: number;
    totalDeptStudents?: number;
    issuePercentage?: number;
  };
  topStudents?: Array<{
    id: string;
    firstName: string;
    lastName: string;
    fullName: string;
    overdueCount: number;
    incompleteCount: number;
  }>;
  topLessons?: Array<{
    lesson: { id: string; title: string };
    overdueCount: number;
    incompleteCount: number;
    studentCount: number;
  }>;
  suggestedTask: {
    title: string;
    description: string;
    departmentIds: string[];
    userIds?: string[];
    frequency: string;
    itemType: string;
  } | null;
  message?: string;
}

interface SuggestionCardProps {
  suggestion: TaskSuggestion;
  onCreateTask?: (suggestion: TaskSuggestion) => void;
  viewMode?: "list" | "grid";
}

export const SuggestionCard: React.FC<SuggestionCardProps> = ({
  suggestion,
  onCreateTask,
  viewMode = "list",
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "HIGH":
        return "bg-red-100 text-red-800 border-red-200";
      case "MEDIUM":
        return "bg-amber-100 text-amber-800 border-amber-200";
      case "LOW":
        return "bg-blue-100 text-blue-800 border-blue-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case "HIGH":
        return "Yüksek Öncelik";
      case "MEDIUM":
        return "Orta Öncelik";
      case "LOW":
        return "Düşük Öncelik";
      default:
        return priority;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "OVERDUE_HOMEWORK":
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case "LESSON_HOMEWORK_ISSUE":
        return <BookOpen className="w-4 h-4 text-amber-500" />;
      case "INDIVIDUAL_HOMEWORK_ISSUE":
        return <Users className="w-4 h-4 text-blue-500" />;
      default:
        return <Lightbulb className="w-4 h-4 text-blue-500" />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "OVERDUE_HOMEWORK":
        return "Şube Bazlı Sorun";
      case "LESSON_HOMEWORK_ISSUE":
        return "Ders Bazlı Sorun";
      case "INDIVIDUAL_HOMEWORK_ISSUE":
        return "Bireysel Takip";
      default:
        return type;
    }
  };

  const getTypeIconBgColor = (type: string) => {
    switch (type) {
      case "OVERDUE_HOMEWORK":
        return "bg-red-100";
      case "LESSON_HOMEWORK_ISSUE":
        return "bg-amber-100";
      case "INDIVIDUAL_HOMEWORK_ISSUE":
        return "bg-blue-100";
      default:
        return "bg-blue-100";
    }
  };

  if (viewMode === "grid") {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow flex flex-col h-full">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <div className="flex-shrink-0">
              <div className={`h-10 w-10 ${getTypeIconBgColor(suggestion.type)} rounded-full flex items-center justify-center`}>
                {getTypeIcon(suggestion.type)}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getPriorityColor(suggestion.priority)}`}>
                  {getPriorityLabel(suggestion.priority)}
                </span>
              </div>
              <span className="text-xs text-gray-500">{getTypeLabel(suggestion.type)}</span>
            </div>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-gray-900 mb-2 break-words">
          {suggestion.suggestedTask?.title || suggestion.message || "Öneri"}
        </h3>

        <p className="text-sm text-gray-600 mb-4 line-clamp-3 leading-relaxed">
          {suggestion.suggestedTask?.description || suggestion.message || ""}
        </p>

        {suggestion.department && (
          <div className="flex items-center gap-1.5 mb-4">
            <div
              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: suggestion.department.color || "#3b82f6" }}
            />
            <span className="text-xs text-gray-600">{suggestion.department.name}</span>
          </div>
        )}

        <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
          <div className="flex items-center gap-1">
            <Users className="w-3.5 h-3.5" />
            <span>{suggestion.stats.studentCount} öğrenci</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5 text-red-500" />
            <span className="text-red-600 font-medium">
              {suggestion.stats.overdueCount} süresi geçmiş
            </span>
          </div>
        </div>

        <div className="mt-auto pt-4 border-t border-gray-100">
          {suggestion.suggestedTask && onCreateTask && (
            <button
              onClick={() => onCreateTask(suggestion)}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Görev Oluştur
            </button>
          )}
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow mb-2">
      <div className="flex flex-col lg:grid lg:grid-cols-12 gap-3 lg:gap-4 lg:items-center">
        {/* Icon and Title */}
        <div className="lg:col-span-4 flex items-start gap-3 min-w-0">
          <div className="flex-shrink-0">
            <div className={`h-8 w-8 ${getTypeIconBgColor(suggestion.type)} rounded-full flex items-center justify-center`}>
              {getTypeIcon(suggestion.type)}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 break-words mb-1">
              {suggestion.suggestedTask?.title || suggestion.message || "Öneri"}
            </h3>
            <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">
              {suggestion.suggestedTask?.description || suggestion.message || ""}
            </p>
          </div>
        </div>

        {/* Priority & Type */}
        <div className="lg:col-span-2 min-w-0">
          <div className="space-y-1">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getPriorityColor(suggestion.priority)}`}>
              {getPriorityLabel(suggestion.priority)}
            </span>
            <div className="text-xs text-gray-500">{getTypeLabel(suggestion.type)}</div>
          </div>
        </div>

        {/* Department */}
        <div className="lg:col-span-2 flex justify-start items-center gap-2">
          {suggestion.department ? (
            <div className="flex items-center gap-1.5">
              <div
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: suggestion.department.color || "#3b82f6" }}
              />
              <span className="text-xs sm:text-sm text-gray-700 font-medium truncate">
                {suggestion.department.name}
              </span>
            </div>
          ) : (
            <span className="text-xs sm:text-sm text-gray-500">-</span>
          )}
        </div>

        {/* Stats */}
        <div className="lg:col-span-2 flex flex-col justify-start items-start lg:items-center gap-0.5">
          <div className="flex items-center gap-1.5 text-xs">
            <Users className="w-3.5 h-3.5 text-gray-500" />
            <span className="text-gray-600">{suggestion.stats.studentCount} öğrenci</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <Clock className="w-3.5 h-3.5 text-red-500" />
            <span className="text-red-600 font-medium">{suggestion.stats.overdueCount} süresi geçmiş</span>
          </div>
        </div>

        {/* Actions */}
        <div className="lg:col-span-2 flex items-center justify-start lg:justify-end gap-2 flex-shrink-0">
          {suggestion.suggestedTask && onCreateTask && (
            <button
              onClick={() => onCreateTask(suggestion)}
              className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium text-white bg-green-600 hover:bg-green-700 transition-colors whitespace-nowrap"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Oluştur</span>
            </button>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="inline-flex items-center gap-1 px-2 py-2 rounded-md text-xs sm:text-sm font-medium text-blue-600 hover:bg-blue-50 transition-colors"
          >
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          {suggestion.topStudents && suggestion.topStudents.length > 0 && (
            <div className="mb-4">
              <h5 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                En Çok Eksik Ödevli Öğrenciler
              </h5>
              <div className="flex flex-wrap gap-2">
                {suggestion.topStudents.map((student) => (
                  <div
                    key={student.id}
                    className="inline-flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5 text-sm"
                  >
                    <span className="font-medium text-gray-800">{student.fullName}</span>
                    <span className="text-red-600 text-xs font-medium">
                      {student.overdueCount} süresi geçmiş
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {suggestion.topLessons && suggestion.topLessons.length > 0 && (
            <div>
              <h5 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                En Çok Sorunlu Dersler
              </h5>
              <div className="flex flex-wrap gap-2">
                {suggestion.topLessons.map((item, idx) => (
                  <div
                    key={idx}
                    className="inline-flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5 text-sm"
                  >
                    <BookOpen className="w-4 h-4 text-gray-400" />
                    <span className="font-medium text-gray-800">{item.lesson.title}</span>
                    <span className="text-xs text-gray-500">
                      {item.studentCount} öğrenci • {item.overdueCount} eksik
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SuggestionCard;
